import { type ReactNode, type FC } from 'react';
import { QueryProvider, type QueryProviderProps } from './QueryProvider';
import { useUIStore, type Toast, type ToastType } from '../stores/ui.store';

/**
 * App Providers
 * 
 * Combines all necessary providers in the correct order.
 */

export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

export interface AppProvidersProps extends Pick<QueryProviderProps, 'config' | 'showDevtools'> {
  children: ReactNode;
  toastPosition?: ToastPosition;
  maxToasts?: number;
}

const POSITION_CLASSES: Record<ToastPosition, string> = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
};

const TYPE_STYLES: Record<ToastType, { bg: string; border: string; text: string; icon: string }> = {
  success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: '✓' },
  error: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: '✕' },
  warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: '⚠' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: 'ℹ' },
};

const ToastItem: FC<{ toast: Toast; onClose: (id: string) => void }> = ({ toast, onClose }) => {
  const styles = TYPE_STYLES[toast.type];
  return (
    <div role="alert" aria-live="polite" className={`${styles.bg} ${styles.border} ${styles.text} p-4 rounded-lg border shadow-lg`}>
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 text-lg leading-none" aria-hidden="true">{styles.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium">{toast.title}</p>
          {toast.description && <p className="text-sm opacity-80 mt-1">{toast.description}</p>}
          {toast.action && (
            <button onClick={toast.action.onClick} className="mt-2 text-sm font-medium underline hover:no-underline">
              {toast.action.label}
            </button>
          )}
        </div>
        <button onClick={() => onClose(toast.id)} className="flex-shrink-0 ml-2 opacity-60 hover:opacity-100 text-xl leading-none" aria-label="Dismiss">×</button>
      </div>
    </div>
  );
};

const ToastContainer: FC<{ position: ToastPosition; maxToasts: number }> = ({ position, maxToasts }) => {
  const toasts = useUIStore((state) => state.toasts);
  const removeToast = useUIStore((state) => state.removeToast);
  const visibleToasts = toasts.slice(0, maxToasts);
  
  if (visibleToasts.length === 0) return null;
  
  return (
    <div className={`fixed ${POSITION_CLASSES[position]} z-50 flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)] pointer-events-none`} aria-label="Notifications">
      {visibleToasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onClose={removeToast} />
        </div>
      ))}
    </div>
  );
};

// Cart expiration is now handled at checkout via payment-intent locking
// No longer need to check for expired items locally

export const AppProviders: FC<AppProvidersProps> = ({
  children,
  config,
  showDevtools,
  toastPosition = 'top-right',
  maxToasts = 5,
}) => {
  return (
    <QueryProvider config={config} showDevtools={showDevtools}>
      {children}
      <ToastContainer position={toastPosition} maxToasts={maxToasts} />
    </QueryProvider>
  );
};